import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardBody } from '@structural-climbing/ui'
import { COLOR_SWATCH } from './ClassCard'
import { classColor, type CalendarEvent, type ClassSession } from './types'
import { timeOf, weekdayNames } from './weekRange'

/** How many classes a day shows before "+N more". */
const PER_DAY = 3

interface Props {
  /** The 42 days of the grid, Monday first. */
  days: string[]
  /** Any day of the month on show; days outside it are dimmed. */
  month: string
  today: string
  classes: ClassSession[]
  events: CalendarEvent[]
  onOpenClass: (session: ClassSession) => void
  onOpenEvent: (event: CalendarEvent) => void
  /** Opens the week holding that day. */
  onOpenDay: (day: string) => void
}

type Entry = { kind: 'class'; session: ClassSession } | { kind: 'event'; event: CalendarEvent }

/**
 * The month at a glance: a line per class, the month's totals on top. A busy
 * day shows its first three and a "+N more" that opens its week.
 */
export function MonthView({
  days,
  month,
  today,
  classes,
  events,
  onOpenClass,
  onOpenEvent,
  onOpenDay,
}: Props) {
  const { t, i18n } = useTranslation()
  const monthKey = month.slice(0, 7)

  // One pass over the classes, not one filter per cell.
  const byDay = useMemo(() => {
    const map = new Map<string, Entry[]>()
    const push = (day: string, entry: Entry) => map.set(day, [...(map.get(day) ?? []), entry])
    for (const session of classes) push(session.starts_at.slice(0, 10), { kind: 'class', session })
    for (const event of events) push(event.starts_at.slice(0, 10), { kind: 'event', event })
    for (const list of map.values())
      list.sort((a, b) =>
        (a.kind === 'class' ? a.session.starts_at : a.event.starts_at).localeCompare(
          b.kind === 'class' ? b.session.starts_at : b.event.starts_at,
        ),
      )
    return map
  }, [classes, events])

  const totals = useMemo(() => {
    const inMonth = classes.filter((c) => c.starts_at.startsWith(monthKey))
    return {
      classes: inMonth.length,
      booked: inMonth.reduce((n, c) => n + c.booked, 0),
      capacity: inMonth.reduce((n, c) => n + c.capacity, 0),
      full: inMonth.filter((c) => c.booked >= c.capacity).length,
    }
  }, [classes, monthKey])

  return (
    <div className="flex flex-col gap-3" data-testid="month-view">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-foreground/70">
        <p>{t('calendar.classesCount', { count: totals.classes })}</p>
        <p>{t('calendar.placesCount', { booked: totals.booked, capacity: totals.capacity })}</p>
        {totals.full > 0 && (
          <p className="text-danger">{t('calendar.fullCount', { count: totals.full })}</p>
        )}
      </div>

      <Card shadow="none" className="border border-divider">
        <CardBody className="p-0">
          <div className="grid grid-cols-7 border-b border-divider bg-content2">
            {weekdayNames(i18n.language).map((name) => (
              <p
                key={name}
                className="px-2 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-foreground/60"
              >
                {name}
              </p>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day, i) => {
              const entries = byDay.get(day) ?? []
              const outside = !day.startsWith(monthKey)
              const isToday = day === today
              const extra = entries.length - PER_DAY
              return (
                <div
                  key={day}
                  className={`flex min-h-28 min-w-0 flex-col gap-1 p-1.5 ${i % 7 ? 'border-l' : ''} ${
                    i >= 7 ? 'border-t' : ''
                  } border-divider ${outside ? 'bg-content2/40 opacity-60' : ''}`}
                >
                  <Button
                    size="sm"
                    variant={isToday ? 'solid' : 'light'}
                    color={isToday ? 'primary' : 'default'}
                    isIconOnly
                    radius="full"
                    className="h-7 w-7 min-w-7 self-start text-xs"
                    aria-label={day}
                    onPress={() => onOpenDay(day)}
                  >
                    {Number(day.slice(8))}
                  </Button>
                  {entries.slice(0, PER_DAY).map((entry) =>
                    entry.kind === 'class' ? (
                      <Button
                        key={entry.session.id}
                        size="sm"
                        variant="light"
                        disableRipple
                        className="h-auto min-h-0 w-full min-w-0 justify-start gap-1.5 px-1.5 py-0.5"
                        onPress={() => onOpenClass(entry.session)}
                      >
                        <div
                          className={`h-2 w-2 shrink-0 rounded-full ${COLOR_SWATCH[classColor(entry.session.color)]}`}
                        />
                        <p className="shrink-0 text-[11px] tabular-nums text-foreground/60">
                          {timeOf(entry.session.starts_at)}
                        </p>
                        <p
                          className={`min-w-0 truncate text-xs ${
                            entry.session.booked >= entry.session.capacity ? 'text-danger' : ''
                          }`}
                        >
                          {entry.session.title}
                        </p>
                      </Button>
                    ) : (
                      <Button
                        key={`${entry.event.kind}-${entry.event.id}`}
                        size="sm"
                        variant="flat"
                        color="secondary"
                        disableRipple
                        className="h-auto min-h-0 w-full min-w-0 justify-start px-1.5 py-0.5"
                        onPress={() => onOpenEvent(entry.event)}
                      >
                        <p className="min-w-0 truncate text-xs">{entry.event.title}</p>
                      </Button>
                    ),
                  )}
                  {extra > 0 && (
                    <Button
                      size="sm"
                      variant="light"
                      disableRipple
                      className="h-auto min-h-0 justify-start px-1.5 py-0.5 text-xs text-foreground/60"
                      onPress={() => onOpenDay(day)}
                    >
                      {t('calendar.moreCount', { count: extra })}
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
